import { Directive, Input, TemplateRef, ViewContainerRef, inject } from '@angular/core';
import { Subscription } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { RequiredPermission, isScopeAllowedForResource } from '../models/settlement-permissions.model';
import { AuthService } from '../services/auth.service';

@Directive({
  selector: '[appHasPermission]',
  standalone: true,
})
export class HasPermissionDirective {
  private tpl = inject(TemplateRef<any>);
  private vcr = inject(ViewContainerRef);
  private auth = inject(AuthService);

  private sub?: Subscription;
  private isViewCreated = false;

  private requirement: RequiredPermission | RequiredPermission[] | null = null;

  @Input('appHasPermission') set setRequirement(value: RequiredPermission | RequiredPermission[] | null) {
    this.requirement = value;
    this.updateView();
  }

  constructor() {
    this.sub = this.auth.currentUser$.pipe(takeUntilDestroyed()).subscribe(() => this.updateView());
  }

  private updateView(): void {
    const canShow = this.checkPermission();

    if (canShow && !this.isViewCreated) {
      this.vcr.clear();
      this.vcr.createEmbeddedView(this.tpl);
      this.isViewCreated = true;
    } else if (!canShow && this.isViewCreated) {
      this.vcr.clear();
      this.isViewCreated = false;
    }
  }

  private checkPermission(): boolean {
    if (!this.requirement) return false;

    const requirements = Array.isArray(this.requirement) ? this.requirement : [this.requirement];

    return requirements.some((req) => {
      if (req.scope && !isScopeAllowedForResource(req.resource, req.scope)) {
        if (typeof ngDevMode !== 'undefined' && ngDevMode) {
          console.warn(`[appHasPermission] Scope "${req.scope}" is not allowed for resource "${req.resource}".`);
        }
        return false;
      }
      return this.auth.hasPermission(req.resource, req.scope);
    });
  }
}
